import { NotificationImageVariant } from "../../api/generated/models/NotificationImageVariant";
import { SingleTransactionNotificationPayload } from "../../api/generated/models/SingleTransactionNotificationPayload";
import { BusinessName, toAbsoluteImageUrl } from "../../api/business";

export interface SingleTransactionFormState {
  amount: string;
  /** Contentful id of the picked merchant, or "" when none is picked. */
  businessContentfulId: string;
  /** Free-text override. When set it wins over the picked merchant's name. */
  businessName: string;
  businessCategory: string;
  imageVariant: NotificationImageVariant;
  /** Raw URL override. When set it wins over the picked merchant's asset. */
  imageUrl: string;
}

/**
 * Builds the request payload. Empty strings are dropped rather than sent, because the backend
 * treats a supplied `businessName` as an override of the merchant resolved from Contentful — an
 * empty string would override the real name with nothing.
 */
export const buildSingleTransactionPayload = (
  form: SingleTransactionFormState
): SingleTransactionNotificationPayload => ({
  amount: Number(form.amount),
  businessName: form.businessName || undefined,
  businessCategory: form.businessCategory || undefined,
  businessContentfulId: form.businessContentfulId || undefined,
  // Only meaningful alongside a merchant; sending it otherwise is noise.
  imageVariant: form.businessContentfulId ? form.imageVariant : undefined,
  imageUrl: form.imageUrl || undefined,
});

/**
 * What the operator will actually see attached to the notification, mirroring the backend's
 * resolution order: an explicit URL wins, then the picked merchant's chosen asset, then nothing.
 *
 * `FIRST_IMAGE` has no preview here: the admin business list returns only the logo and the main
 * image, so the gallery's first image is not known client-side. The notification still sends it —
 * this returns undefined so the UI can say "no preview" rather than show the wrong asset.
 */
export const resolvePreviewImageUrl = (
  form: SingleTransactionFormState,
  business: BusinessName | undefined
): string | undefined => {
  if (form.imageUrl) return form.imageUrl;
  if (!business) return undefined;

  switch (form.imageVariant) {
    case NotificationImageVariant.MAIN_IMAGE:
      return toAbsoluteImageUrl(business.mainImageUrl);
    case NotificationImageVariant.FIRST_IMAGE:
      return undefined;
    default:
      return toAbsoluteImageUrl(business.logoUrl);
  }
};

export const imageVariantOptions: {
  value: NotificationImageVariant;
  label: string;
}[] = [
  { value: NotificationImageVariant.LOGO, label: "Logo (what production sends)" },
  { value: NotificationImageVariant.MAIN_IMAGE, label: "Main image" },
  { value: NotificationImageVariant.FIRST_IMAGE, label: "First gallery image" },
];
